import { useEffect, useRef, useState } from 'react';
import { useImmer } from 'use-immer';
import { useParams } from 'react-router-dom';
import { moduleClientFactory } from '../../../../../api-service/module-client-factory';
import { AttendanceRegistrationTypeDto, AttendanceSheetStateDto, AttendanceStateDto, CreateAttendanceRegistrationDto } from '../../../../../api-service/clients';
import { AttendanceProtocolState, CitizenLessonAttendanceState, RegistrationDetailsEdit } from '../interfaces';
import { downloadAsFile } from '../../../../../utilities';
import { usePdfActions } from '../../../../../components/pdf-actions/use-pdf-actions';

const attendanceClient = moduleClientFactory.createAttendanceProtocolClient();

export interface UseAttendanceProtocolResult {
  state: AttendanceProtocolState;
  isLoading: boolean;
  isSaving: boolean;
  isGeneratingPdf: boolean;
  handleAttendanceStartChange: (citizenId: string, attendanceType?: AttendanceStateDto, keepComment?: boolean) => Promise<void>;
  handleAttendanceEndChange: (citizenId: string, attendanceType?: AttendanceStateDto, keepComment?: boolean) => Promise<void>;
  handleAttendancesStartChange: (attendanceState: AttendanceStateDto) => Promise<void>;
  handleAttendancesEndChange: (attendanceState: AttendanceStateDto) => Promise<void>;
  handleNotRelevantChange: (citizenId: string) => Promise<void>;
  handleNotRelevantsChange: () => Promise<void>;
  handleRegisterNoShows: () => Promise<void>;
  handleSaveRegistrationDetails: (citizenId: string, start?: RegistrationDetailsEdit, end?: RegistrationDetailsEdit) => Promise<void>;
  handleSubmit: () => Promise<void>;
  handleApprove: () => Promise<void>;
  handleCreatePdf: (includeCpr: boolean) => Promise<void>;
  handleOpenPdf: (includeCpr: boolean) => Promise<void>;
  handleDownloadPdf: (includeCpr: boolean) => Promise<void>;
}

export function useAttendanceProtocol(): UseAttendanceProtocolResult {
  const { lessonId } = useParams();

  const [state, setState] = useImmer<AttendanceProtocolState>({
    citizenLessonAttendances: [],
    lessonBeginAt: new Date(),
    lessonEndAt: new Date(),
    courseId: '',
    showStartAttendanceRegistration: false,
    showEndAttendanceRegistration: false,
    requiresApproval: false,
    restrictedCitizenCount: 0,
  });

  const [isLoading, setIsLoading] = useState(true);
  const [isGeneratingPdf, setIsGeneratingPdf] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const pdfActions = usePdfActions();

  // A second click lands before the state a write sets has rendered, so the gate is a ref: it closes
  // during the first click rather than on the render that follows it.
  const isSavingRef = useRef(false);

  async function saveOnce(save: () => Promise<void>) {
    if (isSavingRef.current) {
      return;
    }
    isSavingRef.current = true;
    setIsSaving(true);
    try {
      await save();
    } finally {
      isSavingRef.current = false;
      setIsSaving(false);
    }
  }

  useEffect(() => {
    loadData();
  }, []);

  async function loadData() {
    try {
      setIsLoading(true);
      await fetchProtocol();
    } finally {
      setIsLoading(false);
    }
  }

  // The module decides the time a registration carries, so the protocol is read again after a write rather than guessed at.
  async function fetchProtocol() {
    const result = await attendanceClient.getCitizenLessonAttendancesByLessonId({ lessonId });
    setState(draft => {
      const sortedCitizenLessonAttendances = result.citizenLessonAttendances?.sort((a, b) => a.citizenName!.localeCompare(b.citizenName!));
      draft.citizenLessonAttendances = sortedCitizenLessonAttendances || [];
      draft.courseId = result.courseId;
      draft.courseName = result.courseName;
      draft.lessonBeginAt = result.lesson!.beginAt;
      draft.lessonEndAt = result.lesson!.endAt;
      draft.showStartAttendanceRegistration = result.lesson!.earliestArrivalOnTimeBeforeStart !== undefined;
      draft.showEndAttendanceRegistration = result.lesson!.earliestDepartureOnTimeBeforeEnd !== undefined;
      draft.attendanceSheetState = result.lesson!.attendanceSheetState;
      draft.requiresApproval = result.requiresApproval;
      draft.restrictedCitizenCount = result.restrictedCitizenCount;
    });
  }

  async function handleAttendanceChange(citizenId: string, registrationType: AttendanceRegistrationTypeDto, attendanceType?: AttendanceStateDto, keepComment = false) {
    const isStart = registrationType === AttendanceRegistrationTypeDto.Start;
    const registrationField = isStart ? 'startRegistration' : 'endRegistration';
    if (!attendanceType) {
      await deleteRegistration(citizenId, registrationField);
      return;
    }
    const citizenAttendance = state.citizenLessonAttendances.find(x => x.citizenId === citizenId);
    if (!citizenAttendance) {
      return;
    }

    const existingRegistration = citizenAttendance[registrationField];
    if (existingRegistration?.attendanceRegistrationId) {
      const commentText = keepComment ? existingRegistration.commentText : undefined;
      await attendanceClient.updateAttendanceRegistration({
        updateAttendanceRegistration: {
          attendanceRegistrationId: existingRegistration.attendanceRegistrationId,
          attendanceRegistrationType: registrationType,
          citizenId,
          courseEnrollmentId: citizenAttendance.citizenEnrollmentId,
          lessonId,
          status: attendanceType,
          commentText,
          isDeleted: false,
        },
      });
      setState(draft => {
        const citizenAttendanceDraft = draft.citizenLessonAttendances.find(x => x.citizenId === citizenId);
        if (!citizenAttendanceDraft) {
          return;
        }
        const registration = citizenAttendanceDraft[registrationField];
        if (registration) {
          registration.status = attendanceType;
          registration.commentText = commentText;
        }
      });
    } else {
      const result = await attendanceClient.addAttendanceRegistrations({
        createAttendanceRegistrations: [
          {
            attendanceRegistrationType: registrationType,
            citizenId,
            courseEnrollmentId: citizenAttendance.citizenEnrollmentId,
            lessonId,
            status: attendanceType,
          },
        ],
      });

      setState(draft => {
        result.citizenAttendanceRegistrationIds?.forEach(x => {
          const citizenLessonAttendanceDraft = draft.citizenLessonAttendances.find(c => c.citizenId === x.citizenId);
          if (!citizenLessonAttendanceDraft) {
            return;
          }
          citizenLessonAttendanceDraft[registrationField] = { status: attendanceType, attendanceRegistrationId: x.attendanceRegistrationId };
        });
      });
    }

    if (attendanceType !== AttendanceStateDto.NotRelevant) {
      await deleteNotRelevantSibling(citizenId, registrationType);
    }
  }

  async function deleteNotRelevantSibling(citizenId: string, registrationType: AttendanceRegistrationTypeDto) {
    const siblingField = registrationType === AttendanceRegistrationTypeDto.Start ? 'endRegistration' : 'startRegistration';
    const citizenAttendance = state.citizenLessonAttendances.find(x => x.citizenId === citizenId);
    if (citizenAttendance?.[siblingField]?.status !== AttendanceStateDto.NotRelevant) {
      return;
    }
    await deleteRegistration(citizenId, siblingField);
  }

  async function deleteRegistration(citizenId: string, registrationField: 'startRegistration' | 'endRegistration') {
    const registration = state.citizenLessonAttendances.find(x => x.citizenId === citizenId)?.[registrationField];
    if (!registration?.attendanceRegistrationId) {
      return;
    }
    await attendanceClient.deleteAttendanceRegistration({ attendanceRegistrationId: registration.attendanceRegistrationId });
    setState(draft => {
      const citizenAttendanceDraft = draft.citizenLessonAttendances.find(x => x.citizenId === citizenId);
      if (citizenAttendanceDraft) {
        citizenAttendanceDraft[registrationField] = undefined;
      }
    });
  }

  async function handleAttendancesChange(registrationType: AttendanceRegistrationTypeDto, attendanceState: AttendanceStateDto, citizens = state.citizenLessonAttendances) {
    const isStart = registrationType === AttendanceRegistrationTypeDto.Start;
    const registrationField = isStart ? 'startRegistration' : 'endRegistration';
    const requiresCheckField = isStart ? 'requiresStartCheck' : 'requiresEndCheck';
    const citizenLessonAttendancesToAdd = citizens.filter(x => !x[registrationField] && x[requiresCheckField]);

    const createAttendanceRegistrations = citizenLessonAttendancesToAdd.map(
      x =>
        ({
          attendanceRegistrationType: registrationType,
          citizenId: x.citizenId,
          courseEnrollmentId: x.citizenEnrollmentId,
          lessonId: x.lessonId,
          status: attendanceState,
        } as CreateAttendanceRegistrationDto)
    );
    if (createAttendanceRegistrations.length > 0) {
      const result = await attendanceClient.addAttendanceRegistrations({
        createAttendanceRegistrations,
      });

      setState(draft => {
        result.citizenAttendanceRegistrationIds?.forEach(x => {
          const citizenLessonAttendanceDraft = draft.citizenLessonAttendances.find(c => c.citizenId === x.citizenId);
          if (!citizenLessonAttendanceDraft) {
            return;
          }
          citizenLessonAttendanceDraft[registrationField] = {
            status: attendanceState,
            attendanceRegistrationId: x.attendanceRegistrationId,
          };
        });
      });
    }
  }

  async function handleAttendanceStartChange(citizenId: string, attendanceType?: AttendanceStateDto, keepComment?: boolean) {
    await saveOnce(async () => {
      await handleAttendanceChange(citizenId, AttendanceRegistrationTypeDto.Start, attendanceType, keepComment);
      await fetchProtocol();
    });
  }

  async function handleAttendanceEndChange(citizenId: string, attendanceType?: AttendanceStateDto, keepComment?: boolean) {
    await saveOnce(async () => {
      await handleAttendanceChange(citizenId, AttendanceRegistrationTypeDto.End, attendanceType, keepComment);
      await fetchProtocol();
    });
  }

  async function handleAttendancesStartChange(attendanceState: AttendanceStateDto) {
    await saveOnce(async () => {
      await handleAttendancesChange(AttendanceRegistrationTypeDto.Start, attendanceState);
      await fetchProtocol();
    });
  }

  async function handleAttendancesEndChange(attendanceState: AttendanceStateDto) {
    await saveOnce(async () => {
      await handleAttendancesChange(AttendanceRegistrationTypeDto.End, attendanceState);
      await fetchProtocol();
    });
  }

  async function handleNotRelevantChange(citizenId: string) {
    await saveOnce(async () => {
      const { showStartAttendanceRegistration, showEndAttendanceRegistration } = state;
      const citizenAttendance = state.citizenLessonAttendances.find(x => x.citizenId === citizenId);
      const isNotRelevant =
        citizenAttendance?.startRegistration?.status === AttendanceStateDto.NotRelevant || citizenAttendance?.endRegistration?.status === AttendanceStateDto.NotRelevant;
      if (isNotRelevant) {
        await Promise.all([deleteRegistration(citizenId, 'startRegistration'), deleteRegistration(citizenId, 'endRegistration')]);
        return;
      }
      if (showStartAttendanceRegistration && citizenAttendance?.requiresStartCheck) {
        await handleAttendanceChange(citizenId, AttendanceRegistrationTypeDto.Start, AttendanceStateDto.NotRelevant);
      }
      if (showEndAttendanceRegistration && citizenAttendance?.requiresEndCheck) {
        await handleAttendanceChange(citizenId, AttendanceRegistrationTypeDto.End, AttendanceStateDto.NotRelevant);
      }
      await fetchProtocol();
    });
  }

  async function handleNotRelevantsChange() {
    await saveOnce(async () => {
      const { showStartAttendanceRegistration, showEndAttendanceRegistration, citizenLessonAttendances } = state;
      const unregisteredCitizens = citizenLessonAttendances.filter(x => !x.startRegistration && !x.endRegistration);
      if (showStartAttendanceRegistration) {
        await handleAttendancesChange(AttendanceRegistrationTypeDto.Start, AttendanceStateDto.NotRelevant, unregisteredCitizens);
      }
      if (showEndAttendanceRegistration) {
        await handleAttendancesChange(AttendanceRegistrationTypeDto.End, AttendanceStateDto.NotRelevant, unregisteredCitizens);
      }
      await fetchProtocol();
    });
  }

  async function handleRegisterNoShows() {
    await saveOnce(async () => {
      const result = await attendanceClient.registerNoShows({ lessonId });
      setState(draft => {
        result.registrations?.forEach(registration => {
          const citizenLessonAttendanceDraft = draft.citizenLessonAttendances.find(c => c.citizenId === registration.citizenId);
          if (!citizenLessonAttendanceDraft) {
            return;
          }
          const registrationField = registration.attendanceRegistrationType === AttendanceRegistrationTypeDto.Start ? 'startRegistration' : 'endRegistration';
          citizenLessonAttendanceDraft[registrationField] = { status: registration.status, attendanceRegistrationId: registration.attendanceRegistrationId };
        });
      });
      await fetchProtocol();
    });
  }

  async function updateRegistrationDetails(citizenAttendance: CitizenLessonAttendanceState, registrationType: AttendanceRegistrationTypeDto, details?: RegistrationDetailsEdit) {
    const registrationField = registrationType === AttendanceRegistrationTypeDto.Start ? 'startRegistration' : 'endRegistration';
    const registration = citizenAttendance[registrationField];
    if (!details || !registration?.attendanceRegistrationId || registration.status === undefined) {
      return;
    }
    // Only what the educator typed is sent, never the stored time over again.
    await attendanceClient.updateAttendanceRegistration({
      updateAttendanceRegistration: {
        attendanceRegistrationId: registration.attendanceRegistrationId,
        attendanceRegistrationType: registrationType,
        citizenId: citizenAttendance.citizenId,
        courseEnrollmentId: citizenAttendance.citizenEnrollmentId,
        lessonId,
        status: registration.status,
        checkInTime: details.checkInTime,
        commentText: details.comment,
        isDeleted: false,
      },
    });
    setState(draft => {
      const draftRegistration = draft.citizenLessonAttendances.find(x => x.citizenId === citizenAttendance.citizenId)?.[registrationField];
      if (draftRegistration) {
        draftRegistration.commentText = details.comment;
      }
    });
  }

  async function handleSaveRegistrationDetails(citizenId: string, start?: RegistrationDetailsEdit, end?: RegistrationDetailsEdit) {
    await saveOnce(async () => {
      const citizenAttendance = state.citizenLessonAttendances.find(x => x.citizenId === citizenId);
      if (!citizenAttendance) {
        return;
      }
      await updateRegistrationDetails(citizenAttendance, AttendanceRegistrationTypeDto.Start, start);
      await updateRegistrationDetails(citizenAttendance, AttendanceRegistrationTypeDto.End, end);
      await fetchProtocol();
    });
  }

  async function handleSubmit() {
    await saveOnce(async () => {
      await attendanceClient.submitAttendanceProtocol({ lessonId: lessonId! });
      setState(draft => {
        draft.attendanceSheetState = draft.requiresApproval ? AttendanceSheetStateDto.ReadyForApproval : AttendanceSheetStateDto.Submitted;
      });
    });
  }

  async function handleApprove() {
    await saveOnce(async () => {
      await attendanceClient.approveAttendanceProtocol({ lessonId: lessonId! });
      setState(draft => {
        draft.attendanceSheetState = AttendanceSheetStateDto.Submitted;
      });
    });
  }

  async function handleCreatePdf(includeCpr: boolean) {
    try {
      setIsGeneratingPdf(true);
      const result = await attendanceClient.getAttendanceProtocolPdf({ lessonId: lessonId!, includeCpr });
      downloadAsFile(result.data, 'Protokol.pdf');
    } finally {
      setIsGeneratingPdf(false);
    }
  }

  function fetchProtocolPdf(includeCpr: boolean) {
    return async () => (await attendanceClient.getAttendanceProtocolPdf({ lessonId: lessonId!, includeCpr })).data;
  }

  function handleOpenPdf(includeCpr: boolean) {
    return pdfActions.openPdf(fetchProtocolPdf(includeCpr));
  }

  function handleDownloadPdf(includeCpr: boolean) {
    return pdfActions.downloadFile(fetchProtocolPdf(includeCpr), 'Protokol.pdf');
  }

  return {
    state,
    isLoading,
    isSaving,
    isGeneratingPdf: isGeneratingPdf || pdfActions.isFetching,
    handleAttendanceStartChange,
    handleAttendanceEndChange,
    handleAttendancesStartChange,
    handleAttendancesEndChange,
    handleNotRelevantChange,
    handleNotRelevantsChange,
    handleRegisterNoShows,
    handleSaveRegistrationDetails,
    handleSubmit,
    handleApprove,
    handleCreatePdf,
    handleOpenPdf,
    handleDownloadPdf,
  };
}
